import React from "react";

export type ButtonVariantType = "light" | "dark" | "borderless";
export type ButtonColorVariantType = "primary" | "red" | "gray" | "green";
export type ButtonSizeVariantType = "small" | "large";

const buttonVariantStyles: Record<
  ButtonVariantType,
  Record<ButtonColorVariantType, string>
> = {
  dark: {
    primary:
      "bg-primary-700 text-indigo-text-inverse hover:bg-primary-800 disabled:bg-primary-500 disabled:border-primary-500",
    red: "bg-rose-500 text-indigo-text-on-accent hover:bg-rose-600 disabled:bg-rose-300 disabled:border-rose-300",
    green:
      "bg-olive-500 text-indigo-text-on-accent hover:bg-olive-600 disabled:bg-olive-300 disabled:border-olive-300",
    gray: "bg-indigo-gray-800 text-indigo-text-inverse hover:bg-indigo-gray-900 disabled:bg-indigo-gray-500 disabled:border-indigo-gray-400", // TODO: ask design for color scheme
  },
  light: {
    primary:
      "bg-indigo-canvas-highlight text-primary-700 border-2 border-primary-700 hover:bg-primary-150 disabled:border-primary-500 disabled:text-primary-500",
    red: "bg-indigo-canvas-highlight text-rose-500 border-2 border-rose-500 hover:bg-rose-50 disabled:border-rose-300 disabled:text-rose-300",
    green:
      "bg-indigo-canvas-highlight text-olive-600 border-2 border-olive-600 hover:bg-olive-100 disabled:border-olive-300 disabled:text-olive-300",
    gray: "bg-indigo-canvas-highlight text-indigo-text-primary border-2 border-indigo-gray-800 hover:bg-indigo-canvas-shade disabled:border-indigo-gray-400 disabled:text-indigo-text-tertiary", // TODO: ask design for color scheme
  },
  borderless: {
    primary:
      "bg-indigo-canvas-highlight text-primary-700 hover:bg-primary-150 disabled:text-primary-500 disabled:hover:bg-indigo-canvas-highlight",
    red: "bg-indigo-canvas-highlight text-rose-500 hover:bg-rose-50 disabled:text-rose-300 disabled:hover:bg-indigo-canvas-highlight",
    green:
      "bg-indigo-canvas-highlight text-olive-600 hover:bg-olive-100 disabled:text-olive-300 disabled:hover:bg-indigo-canvas-highlight",
    gray: "bg-indigo-canvas-highlight text-indigo-text-tertiary hover:bg-indigo-canvas-shade disabled:text-indigo-text-disabled disabled:hover:bg-indigo-canvas-highlight",
  },
};

// TODO: REFACTOR!!
// According to Figma, button should only have two size variants: small and large,
// while only padding would differ according to if an icon is present or not.
// Otherwise, the sizes should be fixed.
// Also, it should be able to accept an icon prop

interface ButtonProps {
  children: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  className?: string;
  isVarianted?: boolean;
  width?: string;
  height?: string;
  variant?: ButtonVariantType;
  colorVariant?: ButtonColorVariantType;
  sizeVariant?: ButtonSizeVariantType;
  withIcon?: boolean;
  disabled?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  "data-testid"?: string;
}

const Button: React.FC<ButtonProps> = ({
  children,
  onClick,
  className = "",
  isVarianted = true,
  variant = "light",
  colorVariant = "primary",
  sizeVariant,
  width,
  height,
  withIcon = false,
  disabled = false,
  onMouseEnter,
  onMouseLeave,
  "data-testid": dataTestId,
}) => {
  // Original Button implementation
  const getVariantStyles = () => {
    if (!isVarianted) {
      return className;
    }

    // const baseHeight = "52px";
    const baseHeight = sizeVariant
      ? sizeVariant === "small"
        ? "33px"
        : "44px"
      : "52px";
    const baseWidth = "";
    const baseClass = `h-[${height ? height : baseHeight}] w-[${
      width ? width : baseWidth
    }] flex items-center font-bold justify-center gap-2 py-3 ${
      // withIcon ? "pl-4 pr-5" : "px-5"
      sizeVariant === "small"
        ? withIcon
          ? "pl-2 pr-3"
          : "px-3"
        : withIcon
          ? "pl-4 pr-5"
          : "px-5"
    } rounded-lg focus:outline-none font-inter ${
      sizeVariant === "small" ? "text-sm" : "text-base"
    } transition duration-200 ease-in-out whitespace-nowrap`;
    const variantClass = buttonVariantStyles[variant][colorVariant];
    const variantStyles = `${baseClass} ${variantClass} ${className}`;
    return variantStyles;
  };

  const styles = getVariantStyles();

  return (
    <button
      onClick={onClick}
      className={styles}
      disabled={disabled}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      data-testid={dataTestId}
    >
      {children}
    </button>
  );
};

export default Button;
